var App				= require("./objects/App"),
	ContentTypes	= require("./objects/ContentTypes"),
	Core			= require("./objects/Core"),
	Relationships	= require("./objects/Relationships"),
	Sheet			= require("./data/Sheet"),
	Workbook		= require("./objects/Workbook"),
	Customizing		= require("./binding/Customizing");

/* global JSZip3 */

/**
 * The XLSXBuilder class represents an Office Open XML Spreadsheet
 * (Microsoft Excel) that can be used to insert structured and
 * formatted data and pack a zipped *.xlsx document.
 *
 * XLSXBuilder requires JSZip 3.x to be present.
 *
 * @param {Object[]|string[]} aBinding Binding configuration that is used for {@link Column} definition
 * @param {Object} [oContext] Context object containing metadata for this particular XLSX
 * @param {string} [oContext.application] Application that created this XLSX
 * @param {string} [oContext.version] Application version that was used to create this XLSX
 * @param {string} [oContext.title] Title of the XLSX document
 * @param {string} [oContext.modifiedBy] User context for the exported document
 * @param {string} [oContext.sheetName] The name of the data sheet that will be shown in Excel
 * @param {string} [oContext.metaSheetName] The name of the metadata sheet that will be shown in Excel
 * @param {Object[]} [oContext.metainfo] Optional Metadata that will be displayed in the additional Metadata Sheet
 * @param {string} [hierarchyLevel] Name of the property that contains the hierarchy level of each lime item
 * @param {Object} [mCustomizing] An object containing all customizing related information
 * @param {Object} [mCustomizing.currency] Custom configuration object that can be used for {@link Customizing} digits
 * @param {Object} [mCustomizing.unit] Custom configuration object that can be used for {@link Customizing} digits
 * @param {Object} [mCustomizing.timezone] Custom configuration object used for timezone localization
 *
 * @constructor
 */
function XLSXBuilder(aBinding, oContext, hierarchyLevel, mCustomizing) {
	var aMetadataConfig, i, oMetainfoGroup, sSheetName, sMetadataSheetName;

	if (!(aBinding instanceof Array) || !aBinding.length) {
		throw new Error("No binding provided");
	}

	/* Set default values */
	sSheetName = "SAP Document Export";
	sMetadataSheetName = "Metadata";

	this.app = new this.objects.App(oContext);
	this.core = new this.objects.Core(oContext);
	this.workbook = new this.objects.Workbook();

	/* Configure currency and unit scale settings if available */
	if (mCustomizing && (mCustomizing.currency || mCustomizing.unit)) {
		Customizing.update(mCustomizing);
	}

	/* Overwrite default Sheet names if maintained */
	if (oContext) {
		sSheetName = oContext.sheetName ? oContext.sheetName : sSheetName;
		sMetadataSheetName = oContext.metaSheetName ? oContext.metaSheetName : sMetadataSheetName;
	}

	this.sheet = this.workbook.addSheet(sSheetName, { columns: aBinding, hierarchyLevel: hierarchyLevel });
	this.workbook.selectSheet(this.sheet);

	if (typeof oContext === "object" && oContext.metainfo instanceof Array) {

		/* Define column configuration for metadata sheet */
		aMetadataConfig = [
			{
				property: "key",
				label: "Key",
				width: 20
			},
			{
				property: "value",
				label: "Value",
				width: 20
			}
		];

		/* Add optional Metadata Sheet*/
		this.metadata = this.workbook.addSheet(sMetadataSheetName, { columns: aMetadataConfig, hideHeader: true });

		/*
		 * Add a copy of metadata to the Metadata Sheet. The method
		 * #_insertIntoSheet uses Array#shift and would otherwise
		 * manipulate the original metadata array.
		 */
		for (i = 0; i < oContext.metainfo.length; i++) {
			oMetainfoGroup = oContext.metainfo[i];

			this.metadata.createGroup(oMetainfoGroup.name, 1);
			this._insertIntoSheet(oMetainfoGroup.items.slice(), this.metadata);
		}
	}
}

XLSXBuilder.prototype = {

	objects: {
		App: App,
		ContentTypes: ContentTypes,
		Core: Core,
		Relationships: Relationships,
		Sheet: Sheet,
		Workbook: Workbook
	},

	/**
	 * Triggers the packaging process for the Office Open XML
	 * SpreadSheet. You need to call the method insert() first,
	 * otherwise the generated Document will contain no data.
	 *
	 * The method returns a Promise to allow the library to work
	 * asynchronously.
	 *
	 * @function build
	 * @returns {Promise} Promise that represents a packed zip file as Blob or Nodebuffer
	 */
	build: function() {
		var bFinalize, files, zip, i, j, fileConfig, FileType, xmlObject, relation, zipConfig;

		files = [
			{
				name: "[Content_Types].xml",
				path: "",
				type: this.objects.ContentTypes
			},
			{
				name: ".rels",
				path: "_rels/",
				type: this.objects.Relationships,
				relationships: [
					{
						target: "xl/workbook.xml",
						type: "http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument"
					},
					{
						target: "docProps/app.xml",
						type: "http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties"
					},
					{
						target: "docProps/core.xml",
						type: "http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties"
					}
				]
			}
		];

		zip = new JSZip3();
		bFinalize = true;

		for (i = 0; i < files.length; i++) {
			fileConfig = files[i];
			FileType = fileConfig.type;
			xmlObject = new FileType();

			if (xmlObject instanceof this.objects.Relationships) {
				for (j = 0; j < fileConfig.relationships.length; j++) {
					relation = fileConfig.relationships[j];
					xmlObject.addRelationship(relation.target, relation.type);
				}
			}

			zip.file(fileConfig.path + fileConfig.name, xmlObject.serialize());
		}

		// Add document metadata
		zip.file(this.app.getAbsolutePath(), this.app.serialize());
		zip.file(this.core.getAbsolutePath(), this.core.serialize());

		// Add workbook and its related files
		zip.file(this.workbook.getAbsolutePath(), this.workbook.serialize());
		zip.file(this.workbook.getRelationships().getAbsolutePath(), this.workbook.getRelationships().serialize());

		this.workbook.getRelatedFiles().forEach(function(file) {
			zip.file(file.getAbsolutePath(), file.serialize(bFinalize));
		});

		zipConfig = {
			compression: "DEFLATE",
			type: JSZip3.support.nodebuffer ? "nodebuffer" : "arraybuffer"
		};

		return zip.generateAsync(zipConfig);
	},

	/**
	 * Appends the specified set of data to the Open Document Spreadsheet.
	 * All data will be bound according to the {@link ColumnConfiguration}.
	 *
	 * @function append
	 * @param {(Object[]|Object)} aData - Array of data that gets appended to the Spreadsheet
	 */
	append: function(aData) {
		this._insertIntoSheet(aData, this.sheet);
	},

	/**
	 * Inserts the given data into the specified Sheet. The method will throw an Error if no Sheet is provided.
	 *
	 * @param {Array} aData - Array of business objects that should be inserted
	 * @param {Sheet} oSheet - The Sheet where the data should be inserted into
	 * @private
	 */
	_insertIntoSheet: function(aData, oSheet) {
		var array;

		/* We need to check if param is null or undefined because [null].length results in 1 */
		if (typeof aData === "undefined" || aData === null) {
			return;
		}

		array = aData instanceof Array ? aData : [aData];

		while (array.length) {
			oSheet.insert(array.shift());
		}
	}
};

module.exports = XLSXBuilder;